import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  HardDrive, Users, Layers, FolderGit2, LibraryBig, Clock, Star, Archive, Trash2,
  Folder, FolderPlus, Upload, Search, Download, Eye, MoreHorizontal, ChevronRight,
  X, FileText, Image as ImageIcon, File as FileIcon, RotateCcw, Loader2, ExternalLink, ArrowLeft,
  Sparkles, AlertTriangle, History, LayoutGrid, List as ListIcon, Tag, Pencil, Share2,
  PenLine, ClipboardCheck, FolderInput, CheckCircle2, Check, Plus, UploadCloud,
} from 'lucide-react';
import Header from '../components/Header';
import { useAuth } from '../contexts/AuthContext';
import { useUploader, UploadTray } from '../components/dropdrive/UploadTray';
import { RenameDialog, MoveDialog, ConfirmDialog } from '../components/dropdrive/DriveDialogs';
import { FolderCard, EmptyDrop } from '../components/dropdrive/FolderCard';
import '../components/dropdrive/dropdrive.css';

const API = process.env.REACT_APP_BACKEND_URL;

const SECTIONS = [
  { key: 'my_drive', label: 'My Drive', icon: HardDrive },
  { key: 'client_files', label: 'Client Files', icon: Users, intelligent: true },
  { key: 'application_files', label: 'Application Files', icon: Layers },
  { key: 'team_drive', label: 'Team Drive', icon: FolderGit2 },
  { key: 'templates', label: 'Templates', icon: LibraryBig },
  { key: 'recent', label: 'Recent', icon: Clock },
  { key: 'favourites', label: 'Favourites', icon: Star },
  { key: 'archived', label: 'Archived', icon: Archive },
  { key: 'trash', label: 'Trash', icon: Trash2 },
];

const REAL = ['my_drive', 'team_drive', 'templates'];
const inTrashSection = (s) => ['trash', 'recent', 'favourites', 'archived'].includes(s);
const fmtSize = (b) => (!b && b !== 0 ? '' : b < 1024 ? `${b} B` : b < 1048576 ? `${(b / 1024).toFixed(0)} KB` : `${(b / 1048576).toFixed(1)} MB`);
const fmtDate = (s) => { if (!s) return ''; try { return new Date(s).toLocaleString('en-AU', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }); } catch { return s; } };
const fmtDay = (s) => { if (!s) return '—'; try { return new Date(s).toLocaleDateString('en-AU', { day: '2-digit', month: 'short', year: 'numeric' }); } catch { return s; } };
const relTime = (s) => {
  if (!s) return 'No activity yet';
  const d = new Date(s); const diff = (Date.now() - d.getTime()) / 1000;
  if (diff < 60) return 'just now';
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  if (diff < 604800) return `${Math.floor(diff / 86400)}d ago`;
  return fmtDay(s);
};
const fileIcon = (ct) => (ct || '').startsWith('image/') ? ImageIcon : (ct || '').includes('pdf') ? FileText : FileIcon;
const initials = (n) => (n || '?').split(' ').filter(Boolean).slice(0, 2).map((w) => w[0]).join('').toUpperCase() || '?';
const STATUS_TONE = {
  invited: 'bg-slate-100 text-slate-600', in_progress: 'bg-blue-50 text-blue-700', submitted: 'bg-indigo-50 text-indigo-700',
  settled: 'bg-emerald-50 text-emerald-700', cancelled: 'bg-slate-100 text-slate-500', declined: 'bg-rose-50 text-rose-600',
};
const statusLabel = (s) => (s || '').replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase()) || 'Draft';

export default function DropDrive() {
  const { token } = useAuth();
  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);
  const nav = useNavigate();
  const [sp] = useSearchParams();
  const [beta, setBeta] = useState(null);
  const [overview, setOverview] = useState(null);
  const [section, setSection] = useState(sp.get('section') || 'my_drive');
  const [parent, setParent] = useState(sp.get('parent') || null);
  const [data, setData] = useState({ folders: [], files: [], breadcrumbs: [], writable: false });
  const [loading, setLoading] = useState(false);
  const [view, setView] = useState(localStorage.getItem('dd_view') || 'grid');
  const [menu, setMenu] = useState(null);
  const [panel, setPanel] = useState(null); // { file, url, type, activity }
  const [q, setQ] = useState('');
  const [searchRes, setSearchRes] = useState(null);
  const [drag, setDrag] = useState(false);
  const [versions, setVersions] = useState(null);
  const [needs, setNeeds] = useState(null);
  const [scanning, setScanning] = useState(false);
  const [selected, setSelected] = useState(() => new Set());
  const [ask, setAsk] = useState(null);
  const [askQ, setAskQ] = useState('');
  const [asking, setAsking] = useState(false);
  const fileInput = useRef(null);
  const uploadTarget = useRef(null);
  const [dialog, setDialog] = useState(null);

  const setViewPref = (v) => { setView(v); localStorage.setItem('dd_view', v); };

  const askTarget = useMemo(() => {
    if (!parent) return null;
    if (parent.startsWith('v:app:')) return { scope: 'application', id: parent, label: 'this application' };
    if (parent.startsWith('v:client:')) return { scope: 'client', id: parent, label: 'this client' };
    if (parent.startsWith('v:cat:')) return { scope: 'application', id: `v:app:${parent.split(':')[2]}`, label: 'this application' };
    return null;
  }, [parent]);

  const runAsk = async (question) => {
    const qn = (question || askQ).trim();
    if (!qn || !askTarget) return;
    setAsking(true); setAsk((a) => ({ ...(a || {}), question: qn, answer: null, sources: [] }));
    try {
      const { data } = await axios.post(`${API}/api/dropdrive/ask`, { scope: askTarget.scope, id: askTarget.id, question: qn }, { headers });
      setAsk({ question: qn, answer: data.answer, sources: data.sources || [], docs: data.documents_considered });
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error((d && typeof d === 'object' ? d.message : d) || 'Ask Drop Drive failed');
      setAsk((a) => ({ ...(a || {}), answer: null }));
    } finally { setAsking(false); }
  };

  const loadOverview = useCallback(async () => {
    try { const { data: d } = await axios.get(`${API}/api/dropdrive/overview`, { headers }); setBeta(d.beta_enabled); setOverview(d); }
    catch { setBeta(false); }
  }, [headers]);

  const load = useCallback(async () => {
    setLoading(true); setSearchRes(null);
    try {
      const { data: d } = await axios.get(`${API}/api/dropdrive/tree`, { params: { section, parent: parent || undefined }, headers });
      setData(d);
    } catch (e) { toast.error(e?.response?.data?.detail?.message || 'Could not load Drop Drive'); }
    finally { setLoading(false); }
  }, [section, parent, headers]);

  useEffect(() => { loadOverview(); }, [loadOverview]);
  useEffect(() => { if (beta) load(); }, [beta, load]);

  const go = (sec) => { setSection(sec); setParent(null); clearSel(); };
  const openFolder = (f) => { setParent(f.id); clearSel(); };
  const crumbTo = (id) => setParent(id);

  const onUploaded = useCallback((ok) => { if (ok) toast.success(`${ok} document${ok > 1 ? 's' : ''} added`); load(); loadOverview(); }, [load, loadOverview]);
  const uploader = useUploader({ headers, onDone: onUploaded });
  const uploading = uploader.queue.some((x) => x.state === 'uploading');
  const crumbLabel = () => [SECTIONS.find((x) => x.key === section)?.label, ...(data.breadcrumbs || []).map((c) => c.name)].join(' / ');
  const doUpload = (files, folder) => uploader.add(files, folder
    ? { section, parent: folder.id, label: `${crumbLabel()} / ${folder.name}` }
    : { section, parent, label: crumbLabel() });
  const browse = (folder = null) => { uploadTarget.current = folder; fileInput.current?.click(); };

  const newFolder = async () => {
    const name = window.prompt('Folder name');
    if (!name) return;
    try { await axios.post(`${API}/api/dropdrive/folders`, { section, parent_id: parent, name }, { headers }); toast.success('Folder created'); load(); }
    catch (e) { toast.error(e?.response?.data?.detail?.message || e?.response?.data?.detail || 'Could not create folder'); }
  };

  const patch = async (node, body, msg) => {
    setMenu(null);
    try { await axios.patch(`${API}/api/dropdrive/nodes/${node.id}`, body, { headers }); if (msg) toast.success(msg); load(); loadOverview(); }
    catch (e) { toast.error(e?.response?.data?.detail?.message || e?.response?.data?.detail || 'Action failed'); }
  };

  const rename = (node) => { setMenu(null); setDialog({ kind: 'rename', node }); };
  const move = (node) => { setMenu(null); setDialog({ kind: 'move', node }); };
  const del = (node) => { setMenu(null); setDialog({ kind: 'delete', node }); };
  const doDelete = async (node) => {
    setDialog(null);
    try { await axios.delete(`${API}/api/dropdrive/nodes/${node.id}`, { headers }); toast.success('Deleted'); if (panel?.file?.id === node.id) closePanel(); load(); loadOverview(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Delete failed'); }
  };

  const fetchBlob = async (file, disp) => {
    const res = await axios.get(`${API}/api/dropdrive/file/${file.id}/content`, { params: { disposition: disp }, headers, responseType: 'blob' });
    return res.data;
  };

  const openFile = async (file) => {
    setMenu(null);
    setPanel({ file, url: null, type: null, activity: null, loading: true });
    try {
      const blob = await fetchBlob(file, 'inline');
      let activity = [];
      try { const { data: d } = await axios.get(`${API}/api/dropdrive/activity/${file.id}`, { headers }); activity = d.events || []; } catch { /* noop */ }
      setPanel({ file, url: URL.createObjectURL(blob), type: blob.type, activity, loading: false });
    } catch { toast.error('Could not open file'); setPanel(null); }
  };
  const closePanel = () => { if (panel?.url) URL.revokeObjectURL(panel.url); setPanel(null); };

  const download = async (file) => {
    setMenu(null);
    try {
      const blob = await fetchBlob(file, 'attachment');
      const a = document.createElement('a'); a.href = URL.createObjectURL(blob); a.download = file.name || 'document'; a.click();
      setTimeout(() => URL.revokeObjectURL(a.href), 4000);
    } catch { toast.error('Download failed'); }
  };

  const classifyOne = async (file) => {
    setMenu(null);
    const t = toast.loading('Classifying…');
    try { const { data: m } = await axios.post(`${API}/api/dropdrive/classify`, { file_key: file.id }, { headers });
      toast.success(m.status === 'classified' ? `Identified as ${m.label}` : 'Needs classification', { id: t }); load(); }
    catch { toast.error('Classification failed', { id: t }); }
  };

  const openVersions = async (file) => {
    setMenu(null);
    try { const { data: d } = await axios.get(`${API}/api/dropdrive/versions/${file.id}`, { headers }); setVersions({ file, list: d.versions || [] }); }
    catch { toast.error('Could not load versions'); }
  };

  const openNeeds = async () => {
    try { const { data: d } = await axios.get(`${API}/api/dropdrive/needs-classification`, { headers }); setNeeds(d); }
    catch { toast.error('Could not load review list'); }
  };

  const assignType = async (fileKey, docType) => {
    try { await axios.post(`${API}/api/dropdrive/reclassify`, { file_key: fileKey, doc_type: docType }, { headers }); toast.success('Classified'); openNeeds(); load(); }
    catch { toast.error('Could not classify'); }
  };

  const scanDocs = async () => {
    setScanning(true);
    const t = toast.loading('Scanning documents…');
    try { const { data: r } = await axios.post(`${API}/api/dropdrive/classify-scan`, {}, { headers });
      toast.success(`Classified ${r.classified} document${r.classified === 1 ? '' : 's'}${r.remaining ? ` · ${r.remaining} to go` : ''}`, { id: t }); load(); }
    catch { toast.error('Scan failed', { id: t }); }
    finally { setScanning(false); }
  };

  const comingSoon = (label) => { setMenu(null); toast.info(`${label} is coming soon`); };

  const toggleSel = (id) => setSelected((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const clearSel = () => setSelected(new Set());
  const bulk = async (action) => {
    try { const { data: r } = await axios.post(`${API}/api/dropdrive/bulk`, { ids: [...selected], action }, { headers });
      toast.success(`${r.affected} item${r.affected === 1 ? '' : 's'} updated`); clearSel(); load(); loadOverview(); }
    catch { toast.error('Bulk action failed'); }
  };
  const bulkDownload = async () => {
    const ids = [...selected].join(',');
    try {
      const res = await axios.get(`${API}/api/dropdrive/download-zip`, { params: { ids }, headers, responseType: 'blob' });
      const a = document.createElement('a'); a.href = URL.createObjectURL(res.data); a.download = 'drop-drive.zip'; a.click();
      setTimeout(() => URL.revokeObjectURL(a.href), 4000); clearSel();
    } catch { toast.error('Download failed'); }
  };

  const openClientRecord = async (node) => {
    setMenu(null);
    try { const { data: d } = await axios.get(`${API}/api/dropdrive/resolve/client-record/${node.id}`, { headers }); nav(d.path); }
    catch { toast.error('No linked client record'); }
  };

  const runSearch = async (e) => {
    e?.preventDefault();
    if (!q.trim()) { setSearchRes(null); return; }
    setLoading(true);
    try { const { data: d } = await axios.get(`${API}/api/dropdrive/search`, { params: { q }, headers }); setSearchRes(d); }
    catch { toast.error('Search failed'); }
    finally { setLoading(false); }
  };

  if (beta === false) {
    return (<div className="min-h-screen bg-[#F6F8FB]"><Header /><div className="max-w-3xl mx-auto px-4 py-24 text-center" data-testid="dropdrive-beta-off">
      <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-blue-600 mb-5 shadow-lg shadow-blue-600/20"><HardDrive className="w-8 h-8 text-white" /></div>
      <h1 className="text-2xl font-bold tracking-tight text-slate-900">Drop Drive is in Beta</h1>
      <p className="mt-2 text-slate-500">Drop Drive hasn't been switched on for your account yet. Ask your administrator to enable it.</p>
    </div></div>);
  }

  const isReal = REAL.includes(section);
  const canWrite = isReal && data.writable;
  const canAdd = !searchRes && !inTrashSection(section) && !!data.writable && (isReal || /^v:(app|cat):/.test(parent || ''));
  const folderCanAdd = (f) => (isReal && data.writable) || /^v:(app|cat):/.test(f.id);
  const inTrash = section === 'trash';
  const clientTop = section === 'client_files' && !parent && !searchRes;
  const items = searchRes ? { folders: [], files: searchRes.results } : data;
  const sectionLabel = SECTIONS.find((s) => s.key === section)?.label;

  return (
    <div className="min-h-screen bg-[#F6F8FB]" onClick={() => setMenu(null)}>
      <Header />
      <div className="max-w-[1500px] mx-auto px-3 sm:px-6 py-5">
        <div className="flex flex-col lg:flex-row gap-5">
          {/* Section rail */}
          <nav className="lg:w-60 shrink-0" data-testid="dropdrive-sections">
            <div className="hidden lg:flex items-center gap-2.5 mb-4 px-1">
              <div className="w-9 h-9 rounded-xl bg-blue-600 flex items-center justify-center shrink-0 shadow-md shadow-blue-600/20"><HardDrive className="w-4.5 h-4.5 text-white" /></div>
              <div><p className="text-[15px] font-bold tracking-tight text-slate-900 leading-tight">Drop Drive</p><p className="text-[10px] font-semibold uppercase tracking-wider text-blue-600">Beta</p></div>
            </div>
            {canAdd && (
              <button onClick={() => browse()} disabled={uploading} data-testid="dd-upload"
                className="w-full hidden lg:inline-flex items-center justify-center gap-2 px-4 py-2.5 mb-3 rounded-xl bg-blue-600 text-white text-sm font-semibold hover:bg-blue-700 shadow-md shadow-blue-600/20 disabled:opacity-60 transition-colors">
                {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />} Upload
              </button>
            )}
            <input ref={fileInput} type="file" multiple className="hidden" onChange={(e) => { doUpload(e.target.files, uploadTarget.current); uploadTarget.current = null; e.target.value = ''; }} data-testid="dd-file-input" />
            <div className="flex lg:flex-col gap-1 overflow-x-auto pb-1 lg:pb-0">
              {SECTIONS.map((s) => {
                const Ic = s.icon; const active = s.key === section;
                const count = overview?.counts?.[s.key === 'client_files' ? 'clients' : s.key === 'application_files' ? 'applications' : s.key];
                return (
                  <button key={s.key} onClick={() => go(s.key)} data-testid={`dd-section-${s.key}`}
                    className={`group flex items-center gap-2.5 px-3 py-2.5 rounded-xl text-sm font-semibold whitespace-nowrap transition-all ${active ? 'bg-slate-900 text-white shadow-sm' : 'text-slate-600 hover:bg-white hover:shadow-sm'}`}>
                    <Ic className={`w-4 h-4 shrink-0 ${active ? 'text-white' : 'text-slate-400 group-hover:text-blue-600'}`} /> <span>{s.label}</span>
                    {count ? <span className={`ml-auto text-[11px] font-bold ${active ? 'text-white/60' : 'text-slate-400'}`}>{count}</span> : null}
                  </button>
                );
              })}
            </div>
          </nav>

          {/* Main */}
          <div className="flex-1 min-w-0">
            {/* Search + actions */}
            <div className="flex flex-wrap items-center gap-2.5 mb-4">
              <form onSubmit={runSearch} className="relative flex-1 min-w-[220px]">
                <Search className="w-4.5 h-4.5 text-slate-400 absolute left-3.5 top-1/2 -translate-y-1/2" />
                <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search — try “John Smith payslips” or “latest ANZ statement”" data-testid="dropdrive-search"
                  className="w-full pl-11 pr-9 py-2.5 rounded-xl border border-slate-200 bg-white text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-400 transition" />
                {searchRes && <button type="button" onClick={() => { setQ(''); setSearchRes(null); }} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700"><X className="w-4 h-4" /></button>}
              </form>
              <div className="flex items-center rounded-xl border border-slate-200 bg-white p-0.5 shadow-sm">
                <button onClick={() => setViewPref('grid')} data-testid="dd-view-grid" title="Grid view" className={`p-1.5 rounded-lg ${view === 'grid' ? 'bg-slate-900 text-white' : 'text-slate-400 hover:text-slate-700'}`}><LayoutGrid className="w-4 h-4" /></button>
                <button onClick={() => setViewPref('list')} data-testid="dd-view-list" title="List view" className={`p-1.5 rounded-lg ${view === 'list' ? 'bg-slate-900 text-white' : 'text-slate-400 hover:text-slate-700'}`}><ListIcon className="w-4 h-4" /></button>
              </div>
              <button onClick={scanDocs} disabled={scanning} data-testid="dd-scan" title="Auto-classify new documents" className="inline-flex items-center gap-1.5 px-3 py-2.5 rounded-xl border border-slate-200 bg-white text-sm font-semibold text-slate-700 hover:bg-slate-50 shadow-sm disabled:opacity-60">{scanning ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4 text-violet-500" />} <span className="hidden md:inline">Scan</span></button>
              <button onClick={openNeeds} data-testid="dd-needs" className="inline-flex items-center gap-1.5 px-3 py-2.5 rounded-xl border border-amber-200 bg-amber-50 text-sm font-semibold text-amber-700 hover:bg-amber-100 shadow-sm"><AlertTriangle className="w-4 h-4" /> <span className="hidden md:inline">Review</span></button>
              {askTarget && <button onClick={() => { setAsk({ question: '', answer: null }); setAskQ(''); }} data-testid="dd-ask" className="inline-flex items-center gap-1.5 px-3 py-2.5 rounded-xl bg-violet-600 text-white text-sm font-semibold hover:bg-violet-700 shadow-md shadow-violet-600/20"><Sparkles className="w-4 h-4" /> <span className="hidden md:inline">Ask Drop Drive</span></button>}
              {canAdd && <button onClick={() => browse()} data-testid="dd-add-document" className="inline-flex items-center gap-1.5 px-4 py-2.5 rounded-xl bg-blue-600 text-white text-sm font-semibold hover:bg-blue-700 shadow-md shadow-blue-600/25 transition-[background-color,transform] active:scale-[.97]"><Plus className="w-4 h-4" /> <span>Add Document</span></button>}
              {canWrite && <button onClick={newFolder} data-testid="dd-new-folder" className="inline-flex items-center gap-1.5 px-3 py-2.5 rounded-xl border border-slate-200 bg-white text-sm font-semibold text-slate-700 hover:bg-slate-50 shadow-sm"><FolderPlus className="w-4 h-4" /> <span className="hidden md:inline">New Folder</span></button>}
            </div>

            {/* Breadcrumbs / title */}
            {!searchRes ? (
              <div className="flex items-center gap-1.5 text-sm mb-4 flex-wrap" data-testid="dropdrive-breadcrumbs">
                {parent && <button onClick={() => { const c = data.breadcrumbs || []; setParent(c.length > 1 ? c[c.length - 2].id : null); }} className="p-1.5 -ml-1 rounded-lg hover:bg-white text-slate-500"><ArrowLeft className="w-4 h-4" /></button>}
                <button onClick={() => setParent(null)} className="font-bold tracking-tight text-lg text-slate-900 hover:text-blue-600">{sectionLabel}</button>
                {(data.breadcrumbs || []).map((c) => (<React.Fragment key={c.id}><ChevronRight className="w-4 h-4 text-slate-300" /><button onClick={() => crumbTo(c.id)} className="text-slate-600 hover:text-blue-600 font-medium max-w-[180px] truncate">{c.name}</button></React.Fragment>))}
              </div>
            ) : <p className="text-sm text-slate-500 mb-4" data-testid="dropdrive-search-count">{searchRes.count} result{searchRes.count === 1 ? '' : 's'} for “{searchRes.query}”</p>}

            {selected.size > 0 && (
              <div className="flex items-center gap-2 mb-4 p-2 pl-4 rounded-xl bg-[#0f172a] text-white flex-wrap shadow-lg" data-testid="dropdrive-bulk-bar">
                <span className="text-sm font-semibold">{selected.size} selected</span>
                <span className="flex-1" />
                <button onClick={bulkDownload} data-testid="dd-bulk-download" className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg bg-white/10 hover:bg-white/20 text-sm"><Download className="w-3.5 h-3.5" /> Download</button>
                {isReal && !inTrash && <>
                  <button onClick={() => bulk('favourite')} className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg bg-white/10 hover:bg-white/20 text-sm"><Star className="w-3.5 h-3.5" /> Favourite</button>
                  <button onClick={() => bulk('archive')} className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg bg-white/10 hover:bg-white/20 text-sm"><Archive className="w-3.5 h-3.5" /> Archive</button>
                  <button onClick={() => bulk('trash')} data-testid="dd-bulk-trash" className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg bg-rose-500/80 hover:bg-rose-500 text-sm"><Trash2 className="w-3.5 h-3.5" /> Trash</button>
                </>}
                {inTrash && <button onClick={() => bulk('restore')} className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg bg-white/10 hover:bg-white/20 text-sm"><RotateCcw className="w-3.5 h-3.5" /> Restore</button>}
                <button onClick={clearSel} className="p-1.5 rounded-lg hover:bg-white/20"><X className="w-4 h-4" /></button>
              </div>
            )}

            {/* Content */}
            <div
              onDragOver={(e) => { if (canAdd && e.dataTransfer?.types?.includes('Files')) { e.preventDefault(); setDrag(true); } }}
              onDragLeave={(e) => { if (!e.currentTarget.contains(e.relatedTarget)) setDrag(false); }}
              onDrop={(e) => { if (canAdd) { e.preventDefault(); setDrag(false); doUpload(e.dataTransfer.files); } }}
              className={`relative rounded-2xl transition ${drag ? 'ring-2 ring-blue-500 bg-blue-50/40' : ''} min-h-[320px]`}
              data-testid="dropdrive-content">
              {drag && <div className="dd-drop-overlay absolute inset-0 z-20 flex flex-col items-center justify-center rounded-2xl backdrop-blur-[2px] pointer-events-none" data-testid="dd-drop-overlay"><span className="ring grid h-20 w-20 place-items-center rounded-3xl bg-blue-600 shadow-2xl shadow-blue-600/30"><UploadCloud className="h-10 w-10 text-white" /></span><p className="mt-4 text-base font-bold text-blue-800">Drop to add to {(data.breadcrumbs || []).slice(-1)[0]?.name || sectionLabel}</p><p className="text-xs text-blue-700/70">{data.upload_hint || 'Add as many documents as you like'}</p></div>}
              {canAdd && data.upload_hint && !drag && <p className="mb-3 inline-flex items-center gap-1.5 rounded-lg bg-blue-50 px-2.5 py-1 text-[11px] font-semibold text-blue-700" data-testid="dd-upload-hint"><UploadCloud className="h-3.5 w-3.5" />{data.upload_hint}</p>}
              {loading ? (
                <div className="flex items-center justify-center py-28 text-slate-300"><Loader2 className="w-7 h-7 animate-spin" /></div>
              ) : (items.folders.length === 0 && items.files.length === 0) ? (
                <EmptyDrop canAdd={canAdd} onBrowse={() => browse()} hint={canAdd && data.upload_hint ? '' : 'DerwentAI files them for you.'} fallback={section === 'client_files' ? 'Client workspaces appear automatically as clients are onboarded.' : 'Files will appear here automatically.'} />
              ) : clientTop ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4">
                  {items.folders.map((f) => <ClientCard key={f.id} f={f} onOpen={() => openFolder(f)} onRecord={() => openClientRecord(f)} />)}
                </div>
              ) : view === 'list' ? (
                <ListView items={items} onOpenFolder={openFolder} onOpenFile={openFile} onMenu={setMenu} menu={menu} selected={selected}
                  toggleSel={toggleSel} isReal={isReal} inTrash={inTrash} actions={{ download, rename, move, patch, del, classifyOne, openVersions, comingSoon, openClientRecord }} />
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
                  {items.folders.map((f, i) => (
                    <FolderCard key={f.id} f={f} i={i} canAdd={folderCanAdd(f)} onOpen={() => openFolder(f)} onAdd={() => browse(f)} onDropFiles={(fl) => doUpload(fl, f)} onRecord={() => openClientRecord(f)} />
                  ))}
                  {items.files.map((file) => (
                    <FileCard key={file.id} file={file} selected={selected.has(file.id)} onOpen={() => openFile(file)} onToggle={() => toggleSel(file.id)}
                      menuOpen={menu === file.id} onMenu={(e) => { e.stopPropagation(); setMenu(menu === file.id ? null : file.id); }}
                      isReal={isReal} inTrash={inTrash} actions={{ download, rename, move, patch, del, classifyOne, openVersions, comingSoon }} />
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* File side panel */}
      {panel && <FilePanel panel={panel} onClose={closePanel} actions={{ download, rename, move, del, patch, classifyOne, openVersions, comingSoon }} isReal={isReal} inTrash={inTrash} reload={() => { load(); }} />}

      <UploadTray queue={uploader.queue} onClear={uploader.clear} onUploadAnyway={(it) => uploader.retryDupe(it, { section, parent, label: crumbLabel() })} />
      {dialog?.kind === 'rename' && <RenameDialog node={dialog.node} onClose={() => setDialog(null)} onSave={(name) => { const n = dialog.node; setDialog(null); patch(n, { name }, 'Renamed'); if (panel?.file?.id === n.id) setPanel((pp) => ({ ...pp, file: { ...pp.file, name } })); }} />}
      {dialog?.kind === 'move' && <MoveDialog node={dialog.node} headers={headers} onClose={() => setDialog(null)} onMove={(t) => { const n = dialog.node; setDialog(null); patch(n, { parent_id: t.id }, `Moved to ${t.name}`); if (panel?.file?.id === n.id) closePanel(); }} />}
      {dialog?.kind === 'delete' && <ConfirmDialog title={`Delete “${dialog.node.name}”?`} body={dialog.node.virtual ? 'This removes the document from the client’s file. This can’t be undone.' : 'This permanently deletes it. This can’t be undone.'} onClose={() => setDialog(null)} onOk={() => doDelete(dialog.node)} />}

      {/* Versions modal */}
      {versions && (
        <div className="fixed inset-0 z-50 bg-[#0f172a]/50 backdrop-blur-sm flex items-center justify-center p-3" onClick={() => setVersions(null)} data-testid="dropdrive-versions">
          <div className="bg-white rounded-2xl w-full max-w-md max-h-[80vh] overflow-y-auto shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100 sticky top-0 bg-white">
              <div><p className="text-sm font-bold text-slate-800">Version history</p><p className="text-xs text-slate-400 truncate max-w-[280px]">{versions.file.name}</p></div>
              <button onClick={() => setVersions(null)} className="p-2 rounded-lg hover:bg-slate-100 text-slate-600"><X className="w-4 h-4" /></button>
            </div>
            <ul className="p-3 space-y-2">
              {versions.list.map((v) => (
                <li key={v.file_key} className={`flex items-center gap-3 p-2.5 rounded-xl border ${v.current ? 'border-blue-300 bg-blue-50/40' : 'border-slate-200'}`}>
                  <div className="w-8 h-8 rounded-lg bg-slate-100 flex items-center justify-center shrink-0 text-xs font-bold text-slate-600 font-mono">v{v.version}</div>
                  <div className="min-w-0 flex-1"><p className="text-sm font-semibold text-slate-800 truncate">{v.name} {v.current && <span className="text-[10px] font-bold text-emerald-600">· Current</span>}</p><p className="text-[11px] text-slate-400 font-mono">{fmtSize(v.size)} · {fmtDate(v.uploaded_at)}{v.uploaded_by ? ` · ${v.uploaded_by}` : ''}</p></div>
                  <button onClick={() => download({ id: v.file_key, name: v.name })} className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-500"><Download className="w-4 h-4" /></button>
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}

      {/* Needs classification inbox */}
      {needs && (
        <div className="fixed inset-0 z-50 bg-[#0f172a]/50 backdrop-blur-sm flex items-center justify-center p-3" onClick={() => setNeeds(null)} data-testid="dropdrive-needs-modal">
          <div className="bg-white rounded-2xl w-full max-w-lg max-h-[82vh] overflow-y-auto shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100 sticky top-0 bg-white">
              <div><p className="text-sm font-bold text-slate-800 flex items-center gap-1.5"><AlertTriangle className="w-4 h-4 text-amber-500" /> Needs Classification</p><p className="text-xs text-slate-400">{needs.count} document{needs.count === 1 ? '' : 's'} the AI wasn’t sure about</p></div>
              <button onClick={() => setNeeds(null)} className="p-2 rounded-lg hover:bg-slate-100 text-slate-600"><X className="w-4 h-4" /></button>
            </div>
            <ul className="p-3 space-y-2">
              {needs.items.length === 0 && <li className="text-sm text-slate-400 text-center py-10">Nothing to review — every document is classified.</li>}
              {needs.items.map((it) => (
                <li key={it.file_key} className="p-3 rounded-xl border border-slate-200" data-testid="dd-needs-item">
                  <p className="text-sm font-semibold text-slate-800 truncate">{it.name}</p>
                  <p className="text-[11px] text-slate-400 mb-2">{it.client ? `${it.client} · ` : ''}AI guess: {it.suggested || 'unsure'}</p>
                  <select defaultValue="" onChange={(e) => e.target.value && assignType(it.file_key, e.target.value)} data-testid="dd-needs-select"
                    className="w-full text-sm px-2.5 py-2 rounded-lg border border-slate-200 bg-white">
                    <option value="" disabled>Set document type…</option>
                    {needs.types.map((t) => <option key={t.key} value={t.key}>{t.label}</option>)}
                  </select>
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}

      {/* Ask Drop Drive */}
      {ask && (
        <div className="fixed inset-0 z-50 bg-[#0f172a]/50 backdrop-blur-sm flex items-center justify-center p-3" onClick={() => setAsk(null)} data-testid="dropdrive-ask">
          <div className="bg-white rounded-2xl w-full max-w-lg max-h-[85vh] overflow-y-auto shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100 sticky top-0 bg-white">
              <div><p className="text-sm font-bold text-slate-800 flex items-center gap-1.5"><Sparkles className="w-4 h-4 text-violet-500" /> Ask Drop Drive</p><p className="text-xs text-slate-400">Answers only from documents you can access · {askTarget?.label}</p></div>
              <button onClick={() => setAsk(null)} className="p-2 rounded-lg hover:bg-slate-100 text-slate-600"><X className="w-4 h-4" /></button>
            </div>
            <div className="p-5 space-y-3">
              <form onSubmit={(e) => { e.preventDefault(); runAsk(); }} className="flex gap-2">
                <input autoFocus value={askQ} onChange={(e) => setAskQ(e.target.value)} data-testid="dd-ask-input" placeholder="e.g. What income is shown? What's missing from this file?" className="flex-1 text-sm px-3 py-2.5 rounded-xl border border-slate-200 focus:border-violet-400 focus:ring-2 focus:ring-violet-500/20 outline-none" />
                <button type="submit" disabled={asking || !askQ.trim()} data-testid="dd-ask-send" className="px-4 py-2.5 rounded-xl bg-violet-600 text-white text-sm font-semibold disabled:opacity-50">{asking ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Ask'}</button>
              </form>
              {!ask.answer && !asking && (
                <div className="flex flex-wrap gap-1.5">
                  {["What documents are in this file?", "What's missing for a home loan?", "Summarise the income evidence", "What liabilities are shown?"].map((s) => (
                    <button key={s} onClick={() => { setAskQ(s); runAsk(s); }} className="text-[11px] px-2.5 py-1 rounded-full bg-slate-100 text-slate-600 hover:bg-slate-200">{s}</button>
                  ))}
                </div>
              )}
              {asking && <div className="flex items-center gap-2 text-sm text-slate-500 py-6 justify-center"><Loader2 className="w-4 h-4 animate-spin" /> Reading the documents…</div>}
              {ask.answer && (
                <div data-testid="dd-ask-answer" className="rounded-xl bg-violet-50/60 border border-violet-100 p-3.5">
                  <p className="text-[11px] font-bold text-slate-400 mb-1">{ask.question}</p>
                  <div className="text-sm text-slate-800 whitespace-pre-wrap leading-relaxed">{ask.answer}</div>
                  {ask.sources?.length > 0 && <p className="text-[11px] text-slate-400 mt-2 pt-2 border-t border-violet-100">Based on {ask.docs} document{ask.docs === 1 ? '' : 's'}: {ask.sources.join(', ')}</p>}
                </div>
              )}
              <p className="text-[10px] text-slate-400">AI can make mistakes — verify important figures against the source documents.</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/* ── Client workspace card ─────────────────────────────────────────── */
const READINESS = {
  ready: { label: 'File ready', cls: 'bg-emerald-50 text-emerald-700', dot: 'bg-emerald-500' },
  action_needed: { label: 'Action needed', cls: 'bg-amber-50 text-amber-700', dot: 'bg-amber-500' },
  empty: { label: 'No documents', cls: 'bg-slate-100 text-slate-500', dot: 'bg-slate-300' },
};
function ClientCard({ f, onOpen, onRecord }) {
  const m = f.meta || {};
  const r = READINESS[m.readiness] || READINESS.empty;
  const total = (m.docs || 0) + (m.outstanding || 0);
  const pct = total ? Math.round(((m.docs || 0) / total) * 100) : (m.docs ? 100 : 0);
  return (
    <div onClick={onOpen} data-testid={`dd-client-${f.meta?.client_key || f.id}`}
      className="group relative flex flex-col p-5 rounded-2xl border border-slate-200 bg-white hover:border-blue-300 hover:shadow-lg cursor-pointer transition-all">
      <div className="flex items-start gap-3">
        <div className="w-11 h-11 rounded-full bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center shrink-0 text-white text-sm font-bold">{initials(f.name)}</div>
        <div className="min-w-0 flex-1">
          <p className="text-[15px] font-bold tracking-tight text-slate-900 truncate">{f.name}</p>
          <p className="text-xs text-slate-500 truncate">{m.current_app || `${m.applications || 0} application${m.applications === 1 ? '' : 's'}`}</p>
        </div>
        <button onClick={(e) => { e.stopPropagation(); onRecord(); }} title="Open client record" className="opacity-0 group-hover:opacity-100 p-1.5 rounded-lg hover:bg-slate-100 text-slate-400"><ExternalLink className="w-4 h-4" /></button>
      </div>
      <div className="flex items-center gap-2 mt-3">
        <span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full ${STATUS_TONE[m.current_status] || 'bg-slate-100 text-slate-600'}`}>{statusLabel(m.current_status)}</span>
        <span className={`inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-0.5 rounded-full ${r.cls}`}><span className={`w-1.5 h-1.5 rounded-full ${r.dot}`} />{r.label}</span>
      </div>
      <div className="mt-4">
        <div className="flex items-center justify-between text-[11px] font-medium text-slate-500 mb-1">
          <span>{m.docs || 0} document{m.docs === 1 ? '' : 's'}</span>
          {m.outstanding ? <span className="text-amber-600 font-semibold">{m.outstanding} outstanding</span> : <span className="text-emerald-600">Complete</span>}
        </div>
        <div className="h-1.5 rounded-full bg-slate-100 overflow-hidden"><div className={`h-full rounded-full ${m.outstanding ? 'bg-amber-400' : 'bg-emerald-500'}`} style={{ width: `${pct}%` }} /></div>
      </div>
      <div className="flex items-center gap-1.5 mt-3 text-[11px] text-slate-400">
        <Clock className="w-3 h-3" /> {m.last_upload ? `Last upload ${relTime(m.last_upload)}` : 'No documents uploaded yet'}
      </div>
    </div>
  );
}

/* ── File card (grid) ──────────────────────────────────────────────── */
function Badges({ file }) {
  const cls = file.classification || {};
  return (
    <div className="flex items-center gap-1.5 flex-wrap">
      {cls.status === 'classified' && cls.label && <span className="inline-flex items-center gap-1 text-[10px] font-bold px-1.5 py-0.5 rounded bg-blue-50 text-blue-700" data-testid={`dd-badge-type-${file.id}`}><Tag className="w-2.5 h-2.5" />{cls.label}</span>}
      {cls.status === 'needs_classification' && <span className="inline-flex items-center gap-1 text-[10px] font-bold px-1.5 py-0.5 rounded bg-amber-50 text-amber-700"><AlertTriangle className="w-2.5 h-2.5" />Needs review</span>}
      {file.freshness === 'may_require_update' && <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-orange-50 text-orange-600">May need update</span>}
      {file.freshness === 'expired' && <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-rose-50 text-rose-600">Expired</span>}
    </div>
  );
}
function FileMenu({ file, isReal, inTrash, actions }) {
  const { download, rename, move, patch, del, classifyOne, openVersions, comingSoon } = actions;
  return (
    <div onClick={(e) => e.stopPropagation()} className="dd-pop absolute right-0 top-9 z-30 w-52 bg-white rounded-xl border border-slate-200 shadow-xl py-1 text-sm" data-testid={`dd-menu-panel-${file.id}`}>
      <MI icon={Download} onClick={() => download(file)}>Download</MI>
      <MI icon={Share2} onClick={() => comingSoon('Share')}>Share</MI>
      <MI icon={Sparkles} onClick={() => classifyOne(file)}>Classify</MI>
      <MI icon={History} onClick={() => openVersions(file)}>Versions</MI>
      {!isReal && file.virtual && <>
        <div className="my-1 h-px bg-slate-100" />
        <MI icon={Pencil} onClick={() => rename(file)}>Rename</MI>
        <MI icon={FolderInput} onClick={() => move(file)}>Move to category</MI>
        <MI icon={Trash2} onClick={() => del(file)} danger>Delete</MI>
      </>}
      {isReal && !inTrash && <>
        <div className="my-1 h-px bg-slate-100" />
        <MI icon={Star} onClick={() => patch(file, { favourite: !file.favourite }, file.favourite ? 'Removed from favourites' : 'Added to favourites')}>{file.favourite ? 'Unfavourite' : 'Favourite'}</MI>
        <MI icon={Pencil} onClick={() => rename(file)}>Rename</MI>
        <MI icon={FolderInput} onClick={() => move(file)}>Move</MI>
        <MI icon={Archive} onClick={() => patch(file, { archived: !file.archived }, file.archived ? 'Unarchived' : 'Archived')}>{file.archived ? 'Unarchive' : 'Archive'}</MI>
        <div className="my-1 h-px bg-slate-100" />
        <MI icon={PenLine} onClick={() => comingSoon('Send to E-Sign')}>Send to E-Sign</MI>
        <MI icon={ClipboardCheck} onClick={() => comingSoon('Use in Credit Assessment')}>Use in Credit Assessment</MI>
        <MI icon={Trash2} onClick={() => patch(file, { trashed: true }, 'Moved to Trash')} danger>Move to Trash</MI>
      </>}
      {inTrash && <>
        <div className="my-1 h-px bg-slate-100" />
        <MI icon={RotateCcw} onClick={() => patch(file, { trashed: false }, 'Restored')}>Restore</MI>
        <MI icon={Trash2} onClick={() => del(file)} danger>Delete forever</MI>
      </>}
    </div>
  );
}
function FileCard({ file, selected, onOpen, onToggle, menuOpen, onMenu, isReal, inTrash, actions }) {
  const Ic = fileIcon(file.content_type);
  return (
    <div data-testid={`dd-file-${file.id}`}
      className={`group relative flex flex-col p-4 rounded-2xl border bg-white transition-all ${selected ? 'border-blue-500 ring-1 ring-blue-500/30' : 'border-slate-200 hover:border-blue-300 hover:shadow-md'}`}>
      <button onClick={onToggle} data-testid={`dd-select-${file.id}`} className={`absolute top-3 left-3 w-4.5 h-4.5 rounded-md border flex items-center justify-center transition ${selected ? 'bg-blue-600 border-blue-600 text-white' : 'border-slate-300 bg-white opacity-0 group-hover:opacity-100'}`}>{selected && <Check className="w-3 h-3" />}</button>
      {/* hover quick actions */}
      <div className="absolute top-2.5 right-2.5 flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition">
        <button onClick={(e) => { e.stopPropagation(); onOpen(); }} title="Preview" className="p-1.5 rounded-lg bg-white border border-slate-200 shadow-sm text-slate-500 hover:text-blue-600"><Eye className="w-3.5 h-3.5" /></button>
        <button onClick={(e) => { e.stopPropagation(); actions.comingSoon('Share'); }} title="Share" className="p-1.5 rounded-lg bg-white border border-slate-200 shadow-sm text-slate-500 hover:text-blue-600"><Share2 className="w-3.5 h-3.5" /></button>
        <div className="relative">
          <button onClick={onMenu} data-testid={`dd-menu-${file.id}`} title="More" className="p-1.5 rounded-lg bg-white border border-slate-200 shadow-sm text-slate-500 hover:text-slate-900"><MoreHorizontal className="w-3.5 h-3.5" /></button>
          {menuOpen && <FileMenu file={file} isReal={isReal} inTrash={inTrash} actions={actions} />}
        </div>
      </div>
      <button onClick={onOpen} className="flex flex-col items-start text-left w-full">
        <div className="w-12 h-12 rounded-xl bg-slate-50 flex items-center justify-center mb-3 mt-1"><Ic className={`w-6 h-6 ${(file.content_type || '').includes('pdf') ? 'text-rose-500' : (file.content_type || '').startsWith('image/') ? 'text-blue-500' : 'text-slate-400'}`} /></div>
        <p className="text-sm font-semibold text-slate-800 truncate w-full flex items-center gap-1">{file.favourite && <Star className="w-3 h-3 fill-amber-400 text-amber-400 shrink-0" />}{file.name}</p>
        <div className="mt-1.5"><Badges file={file} /></div>
        <p className="text-[11px] text-slate-400 truncate w-full mt-1.5 font-mono">{file.client ? `${file.client} · ` : ''}{fmtSize(file.size)} · {fmtDay(file.uploaded_at || file.created_at)}</p>
      </button>
    </div>
  );
}

/* ── List view ─────────────────────────────────────────────────────── */
function ListView({ items, onOpenFolder, onOpenFile, onMenu, menu, selected, toggleSel, isReal, inTrash, actions }) {
  return (
    <div className="rounded-2xl border border-slate-200 bg-white overflow-hidden" data-testid="dropdrive-list">
      <div className="hidden md:grid grid-cols-[1.6fr_0.7fr_1fr_0.8fr_0.8fr_auto] gap-3 px-4 py-2.5 border-b border-slate-100 text-[11px] font-semibold uppercase tracking-wide text-slate-400">
        <span>Name</span><span>Type</span><span>Client / App</span><span>Uploaded</span><span>By</span><span className="w-8" />
      </div>
      {items.folders.map((f) => (
        <div key={f.id} onClick={() => onOpenFolder(f)} data-testid={`dd-folder-${f.id}`} className="grid grid-cols-[1fr_auto] md:grid-cols-[1.6fr_0.7fr_1fr_0.8fr_0.8fr_auto] gap-3 px-4 py-3 items-center border-b border-slate-50 hover:bg-slate-50 cursor-pointer">
          <div className="flex items-center gap-2.5 min-w-0"><Folder className="w-4.5 h-4.5 text-amber-500 shrink-0" /><span className="text-sm font-semibold text-slate-800 truncate">{f.name}</span></div>
          <span className="hidden md:block text-xs text-slate-400">Folder</span>
          <span className="hidden md:block text-xs text-slate-400 truncate">{f.meta?.applications != null ? `${f.meta.applications} app${f.meta.applications === 1 ? '' : 's'}` : ''}</span>
          <span className="hidden md:block" /><span className="hidden md:block" /><span className="w-8" />
        </div>
      ))}
      {items.files.map((file) => {
        const cls = file.classification || {};
        const sel = selected.has(file.id);
        const Ic = fileIcon(file.content_type);
        return (
          <div key={file.id} data-testid={`dd-file-${file.id}`} className={`group grid grid-cols-[1fr_auto] md:grid-cols-[1.6fr_0.7fr_1fr_0.8fr_0.8fr_auto] gap-3 px-4 py-3 items-center border-b border-slate-50 transition ${sel ? 'bg-blue-50/50' : 'hover:bg-slate-50'}`}>
            <div className="flex items-center gap-2.5 min-w-0">
              <button onClick={() => toggleSel(file.id)} data-testid={`dd-select-${file.id}`} className={`w-4 h-4 rounded border flex items-center justify-center shrink-0 ${sel ? 'bg-blue-600 border-blue-600 text-white' : 'border-slate-300'}`}>{sel && <Check className="w-3 h-3" />}</button>
              <button onClick={() => onOpenFile(file)} className="flex items-center gap-2 min-w-0"><Ic className={`w-4 h-4 shrink-0 ${(file.content_type || '').includes('pdf') ? 'text-rose-500' : 'text-slate-400'}`} /><span className="text-sm font-semibold text-slate-800 truncate flex items-center gap-1">{file.favourite && <Star className="w-3 h-3 fill-amber-400 text-amber-400" />}{file.name}</span></button>
            </div>
            <div className="hidden md:block text-xs truncate">{cls.status === 'classified' && cls.label ? <span className="text-blue-700 font-medium">{cls.label}</span> : cls.status === 'needs_classification' ? <span className="text-amber-600">Needs review</span> : <span className="text-slate-400">Document</span>}</div>
            <span className="hidden md:block text-xs text-slate-500 truncate">{file.client || file.category || '—'}</span>
            <span className="hidden md:block text-xs text-slate-400 font-mono">{fmtDay(file.uploaded_at || file.created_at)}</span>
            <span className="hidden md:block text-xs text-slate-400 truncate">{file.uploaded_by_name || '—'}</span>
            <div className="relative flex items-center justify-end">
              <button onClick={(e) => { e.stopPropagation(); onMenu(menu === file.id ? null : file.id); }} data-testid={`dd-menu-${file.id}`} className="p-1.5 rounded-lg hover:bg-slate-200 text-slate-400"><MoreHorizontal className="w-4 h-4" /></button>
              {menu === file.id && <FileMenu file={file} isReal={isReal} inTrash={inTrash} actions={actions} />}
            </div>
          </div>
        );
      })}
    </div>
  );
}

/* ── File side panel ───────────────────────────────────────────────── */
function FilePanel({ panel, onClose, actions, isReal, inTrash }) {
  const { file } = panel;
  const cls = file.classification || {};
  const { download, rename, move, del, patch, classifyOne, openVersions, comingSoon } = actions;
  return (
    <div className="fixed inset-0 z-50 flex justify-end" data-testid="dd-file-panel">
      <div className="absolute inset-0 bg-[#0f172a]/40 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-white w-full max-w-[520px] h-full flex flex-col shadow-2xl animate-in slide-in-from-right" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100">
          <div className="flex items-center gap-2.5 min-w-0">
            {React.createElement(fileIcon(panel.type || file.content_type), { className: `w-5 h-5 shrink-0 ${(panel.type || '').includes('pdf') ? 'text-rose-500' : 'text-slate-400'}` })}
            <p className="text-sm font-bold text-slate-800 truncate" data-testid="dd-panel-name">{file.name}</p>
          </div>
          <button onClick={onClose} data-testid="dd-panel-close" className="p-2 rounded-lg hover:bg-slate-100 text-slate-600 shrink-0"><X className="w-4 h-4" /></button>
        </div>
        {/* preview */}
        <div className="bg-slate-100 flex items-center justify-center overflow-auto" style={{ height: '42%' }}>
          {panel.loading ? <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
            : panel.type?.startsWith('image/') ? <img src={panel.url} alt={file.name} className="max-w-full max-h-full object-contain" />
              : panel.type?.includes('pdf') ? <iframe title="preview" src={panel.url} className="w-full h-full" />
                : <div className="text-center p-8"><FileIcon className="w-10 h-10 text-slate-300 mx-auto mb-2" /><p className="text-slate-500 text-sm">Preview isn't available for this file type.</p></div>}
        </div>
        <div className="flex-1 overflow-y-auto p-5 space-y-5">
          {/* action buttons */}
          <div className="grid grid-cols-4 gap-2">
            <PanelAction icon={Download} label="Download" onClick={() => download(file)} testId="dd-panel-download" />
            <PanelAction icon={Share2} label="Share" onClick={() => comingSoon('Share')} />
            {(isReal || file.virtual) && !inTrash && <PanelAction icon={Pencil} label="Rename" onClick={() => rename(file)} testId="dd-panel-rename" />}
            {(isReal || file.virtual) && !inTrash && <PanelAction icon={FolderInput} label="Move" onClick={() => move(file)} testId="dd-panel-move" />}
            {(isReal || file.virtual) && !inTrash && <PanelAction icon={Trash2} label="Delete" onClick={() => { if (isReal) { patch(file, { trashed: true }, 'Moved to Trash'); onClose(); } else del(file); }} testId="dd-panel-delete" />}
            {isReal && !inTrash && <PanelAction icon={Star} label={file.favourite ? 'Unfav' : 'Favourite'} onClick={() => patch(file, { favourite: !file.favourite }, file.favourite ? 'Removed from favourites' : 'Added to favourites')} active={file.favourite} />}
            <PanelAction icon={PenLine} label="E-Sign" onClick={() => comingSoon('Send to E-Sign')} />
            <PanelAction icon={ClipboardCheck} label="Credit" onClick={() => comingSoon('Use in Credit Assessment')} />
            <PanelAction icon={History} label="Versions" onClick={() => openVersions(file)} />
            <PanelAction icon={Sparkles} label="Classify" onClick={() => classifyOne(file)} tone="violet" />
          </div>
          {/* metadata */}
          <div>
            <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-400 mb-2">Details</p>
            <dl className="space-y-2 text-sm">
              <Meta label="Type">{cls.status === 'classified' && cls.label ? <span className="inline-flex items-center gap-1 text-xs font-bold px-2 py-0.5 rounded bg-blue-50 text-blue-700"><Tag className="w-2.5 h-2.5" />{cls.label}</span> : cls.status === 'needs_classification' ? <span className="text-xs font-bold text-amber-600">Needs review</span> : <span className="text-slate-500">Document</span>}</Meta>
              {file.client && <Meta label="Client">{file.client}</Meta>}
              {file.category && <Meta label="Category">{file.category}</Meta>}
              <Meta label="Uploaded">{fmtDate(file.uploaded_at || file.created_at)}</Meta>
              {file.uploaded_by_name && <Meta label="Uploaded by">{file.uploaded_by_name}</Meta>}
              <Meta label="Size"><span className="font-mono">{fmtSize(file.size)}</span></Meta>
              {file.freshness === 'may_require_update' && <Meta label="Freshness"><span className="text-orange-600 font-semibold">May need update</span></Meta>}
              {file.freshness === 'expired' && <Meta label="Freshness"><span className="text-rose-600 font-semibold">Expired</span></Meta>}
            </dl>
          </div>
          {/* activity */}
          <div>
            <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-400 mb-2">Activity</p>
            {(!panel.activity || panel.activity.length === 0) ? <p className="text-sm text-slate-400">No activity recorded yet.</p> : (
              <ul className="space-y-3">
                {panel.activity.map((e, i) => (
                  <li key={i} className="flex gap-3 text-sm">
                    <div className="mt-1.5 w-2 h-2 rounded-full bg-blue-400 shrink-0" />
                    <div><p className="text-slate-700"><span className="font-semibold">{e.actor_name || 'System'}</span> — {String(e.action || '').replace(/([A-Z])/g, ' $1').toLowerCase().trim()}</p><p className="text-[11px] text-slate-400 font-mono">{fmtDate(e.at)}</p></div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
const PanelAction = ({ icon: Ic, label, onClick, testId, active, tone }) => (
  <button onClick={onClick} data-testid={testId} className={`flex flex-col items-center gap-1 py-2.5 rounded-xl border text-[11px] font-semibold transition ${active ? 'border-amber-300 bg-amber-50 text-amber-700' : tone === 'violet' ? 'border-violet-200 bg-violet-50 text-violet-700 hover:bg-violet-100' : 'border-slate-200 text-slate-600 hover:bg-slate-50'}`}>
    <Ic className="w-4 h-4" /> {label}
  </button>
);
const Meta = ({ label, children }) => (
  <div className="flex items-start gap-3"><dt className="w-24 shrink-0 text-slate-400">{label}</dt><dd className="flex-1 text-slate-700 font-medium min-w-0">{children}</dd></div>
);

const MI = ({ icon: Ic, onClick, children, danger }) => (
  <button onClick={onClick} className={`w-full flex items-center gap-2.5 px-3 py-2 text-left hover:bg-slate-50 ${danger ? 'text-rose-600' : 'text-slate-700'}`}><Ic className="w-4 h-4" /> {children}</button>
);
